import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { getRemoteValue, saveRemote, hasUnsyncedLocalWrite } from './remotePreferences'

const STORAGE_KEY = 'chart_indicator_state_v2'

// Bug P0 (2026-08-13, "un click en un ojo apaga 2-3 medias"): este contador
// era un `let` a nivel de módulo, reiniciado a 0 en CADA carga de página. Los
// 3 DEFAULT_MOVING_AVERAGES ya consumían ma-1/ma-2/ma-3 en cada carga, así
// que la PRIMERA media añadida por el usuario en CUALQUIER sesión nueva
// siempre generaba "ma-4" — sin importar cuántas medias con ese mismo id ya
// existieran en el servidor de sesiones anteriores. El backend hace merge
// por clave (PATCH), no valida unicidad de id dentro del array, así que
// colisiones se acumulan calladamente. Con varias medias compartiendo id,
// toggleMovingAverage/updateMovingAverage (que hacen `.map(ma => ma.id ===
// id ? ... : ma)`) tocan TODAS las que compartan ese id en un solo click —
// reproducido en la cuenta real de Marc: 3 medias con id "ma-4" (EMA 52/100/
// 200), un click en el ojo de EMA 200 apagaba las tres. Mismo origen del
// duplicado en el log de notificaciones (buildWatches/buildClosedBars
// indexan por ma.id — dos medias con el mismo id se pisan la una a la otra).
// Fix: id globalmente único (timestamp + sufijo aleatorio), no un contador
// secuencial que reinicia — mismo patrón ya usado en Panel.jsx (fallback
// addMa) y constants.js (resolveConfig), simplemente nunca se aplicó aquí,
// que es la única fuente real de ids para movingAverages.
const nextId = () => `ma-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

const DEFAULT_LINE_STYLE = 'solid'
const DEFAULT_LINE_WIDTH = 2

const DEFAULT_MOVING_AVERAGES = [
  { id: nextId(), type: 'ema', period: 20, color: '#f59e0b', active: true, lineStyle: DEFAULT_LINE_STYLE, lineWidth: DEFAULT_LINE_WIDTH },
  { id: nextId(), type: 'ema', period: 50, color: '#0B3D2E', active: true, lineStyle: DEFAULT_LINE_STYLE, lineWidth: DEFAULT_LINE_WIDTH },
  { id: nextId(), type: 'sma', period: 200, color: '#ef4444', active: true, lineStyle: DEFAULT_LINE_STYLE, lineWidth: DEFAULT_LINE_WIDTH },
]

const DEFAULTS = {
  _version: 16,
  movingAverages: DEFAULT_MOVING_AVERAGES,
  showRsi: false,
  showVolume: false,
  showStructureLiquidityPools: false,
  activeRegistry: {},
}

const LEGACY_REGISTRY_IDS = ['breaker_block', 'mitigation_block']

function migrate(parsed) {
  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return DEFAULTS
  }
  const migrated = { ...DEFAULTS, ...parsed }
  if (!Array.isArray(migrated.movingAverages)) {
    const list = []
    if (parsed.showEma20) list.push({ id: nextId(), type: 'ema', period: 20, color: '#f59e0b', active: true })
    if (parsed.showEma50) list.push({ id: nextId(), type: 'ema', period: 50, color: '#0B3D2E', active: true })
    if (parsed.showSma200) list.push({ id: nextId(), type: 'sma', period: 200, color: '#ef4444', active: true })
    migrated.movingAverages = list.length ? list : DEFAULT_MOVING_AVERAGES
  }
  if (parsed.showVwap && !migrated.activeRegistry?.vwap) {
    migrated.activeRegistry = { ...migrated.activeRegistry, vwap: true }
  }
  if (parsed.showBollinger && !migrated.activeRegistry?.bollinger_bands_quantum) {
    migrated.activeRegistry = { ...migrated.activeRegistry, bollinger_bands_quantum: true }
  }
  if (parsed.showICT === true || parsed.showi_ict === true) {
    migrated.activeRegistry = { ...migrated.activeRegistry, 'ict_quantum': true }
  }
  delete migrated.showVwap
  delete migrated.showBollinger
  delete migrated.showICT
  delete migrated.showi_ict
  if (parsed.showStructureBreaks === true && !migrated.activeRegistry?.['choch_bos_quantum']) {
    migrated.activeRegistry = { ...migrated.activeRegistry, 'choch_bos_quantum': true }
  }
  delete migrated.showStructureBreaks
  if (parsed.showStructureFVG === true && !migrated.activeRegistry?.['fvg_ifvg_quantum']) {
    migrated.activeRegistry = { ...migrated.activeRegistry, 'fvg_ifvg_quantum': true }
  }
  delete migrated.showStructureFVG
  delete migrated.showStructureBISI
  delete migrated.showStructureSIBI
  delete migrated.showStructureIFVG
  if (parsed.showStructureOB === true && !migrated.activeRegistry?.['order_blocks_quantum']) {
    migrated.activeRegistry = { ...migrated.activeRegistry, 'order_blocks_quantum': true }
  }
  delete migrated.showStructureOB
  delete migrated.showStructureBreakerBlock
  delete migrated.showStructureMitigationBlock
  if (migrated.activeRegistry && typeof migrated.activeRegistry === 'object') {
    for (const legacyId of LEGACY_REGISTRY_IDS) {
      if (legacyId in migrated.activeRegistry) {
        const { [legacyId]: _removed, ...rest } = migrated.activeRegistry
        migrated.activeRegistry = rest
      }
    }
  }
  if (migrated.activeRegistry?.bollinger === true) {
    migrated.activeRegistry = { ...migrated.activeRegistry, bollinger_bands_quantum: true }
    delete migrated.activeRegistry.bollinger
  }
  delete migrated.showStructureBollinger
  if (migrated.activeRegistry && typeof migrated.activeRegistry === 'object' && 'bollinger' in migrated.activeRegistry) {
    const { bollinger: _removedBollinger, ...rest } = migrated.activeRegistry
    migrated.activeRegistry = rest
  }
  if (migrated.activeRegistry && typeof migrated.activeRegistry === 'object' && 'breakout_pattern' in migrated.activeRegistry) {
    if (migrated.activeRegistry.breakout_pattern === true) {
      migrated.activeRegistry = { ...migrated.activeRegistry, patron_ruptura_quantum: true }
    }
    delete migrated.activeRegistry.breakout_pattern
  }
  if (migrated.activeRegistry && typeof migrated.activeRegistry === 'object' && 'showi_ict' in migrated.activeRegistry) {
    if (migrated.activeRegistry.showi_ict === true) {
      migrated.activeRegistry = { ...migrated.activeRegistry, showict_quantum: true }
    }
    delete migrated.activeRegistry.showi_ict
  }
  if ((parsed._version ?? 0) < 14 && Array.isArray(migrated.movingAverages) && migrated.movingAverages.length > 0) {
    migrated.activeRegistry = { ...migrated.activeRegistry, ema_sma_quantum: true }
  }
  if ((parsed._version ?? 0) < 15 && Array.isArray(migrated.movingAverages)) {
    const seen = new Map()
    const deduped = []
    for (const ma of migrated.movingAverages) {
      if (ma == null || typeof ma !== 'object') continue
      const key = `${ma.type}:${ma.period}`
      if (!seen.has(key)) {
        seen.set(key, true)
        deduped.push(ma)
      }
    }
    migrated.movingAverages = deduped
  }
  // Saneado de IDs duplicados (2026-08-13, no gateado por _version: a
  // diferencia de las migraciones de arriba, esta corrupción no viene de un
  // formato viejo de una versión concreta — se acumuló silenciosamente en
  // CUALQUIER versión mientras nextId() era un contador que reiniciaba en
  // cada carga de página (ver el comentario de nextId más arriba). Por eso
  // corre SIEMPRE, en cada carga, y no una sola vez con un `if (_version <
  // N)`: una cuenta ya migrada a v16 (como la de Marc) seguía acumulando
  // colisiones nuevas cada vez que añadía una media en una sesión fresca, así
  // que "una vez y listo" no basta para autocurar cuentas ya afectadas.
  // NUNCA se borra una media por esto — a diferencia del dedup de v15 (que
  // sí descarta duplicados legítimos de type+period), un id compartido entre
  // dos medias con type/period DISTINTOS son dos medias reales del usuario
  // (p.ej. EMA 52 y EMA 100 con el mismo id "ma-4") — perderlas sería
  // destructivo. Se conserva la primera ocurrencia de cada id tal cual y se
  // reasigna un id nuevo (único) a cada repetición siguiente.
  if (Array.isArray(migrated.movingAverages)) {
    const seenIds = new Set()
    migrated.movingAverages = migrated.movingAverages.map((ma) => {
      if (ma == null || typeof ma !== 'object' || ma.id == null) return ma
      if (!seenIds.has(ma.id)) {
        seenIds.add(ma.id)
        return ma
      }
      const freshId = nextId()
      seenIds.add(freshId)
      return { ...ma, id: freshId }
    })
  }
  // v16 (2026-08-12): estilo de línea (sólida/discontinua/punteada) y
  // grosor (1-4px) por media. Aditiva e idempotente — sólo RELLENA los
  // campos ausentes con el default (sólida, 2px); las medias ya guardadas
  // de un usuario (con o sin estos campos) nunca se tocan ni se recrean, así
  // que siguen renderizando exactamente igual que antes de esta versión.
  if (Array.isArray(migrated.movingAverages)) {
    migrated.movingAverages = migrated.movingAverages.map((ma) => {
      if (ma == null || typeof ma !== 'object') return ma
      if (ma.lineStyle !== undefined && ma.lineWidth !== undefined) return ma
      return {
        ...ma,
        lineStyle: ma.lineStyle ?? DEFAULT_LINE_STYLE,
        lineWidth: ma.lineWidth ?? DEFAULT_LINE_WIDTH,
      }
    })
  }
  migrated._version = DEFAULTS._version
  return migrated
}

function loadState() {
  if (typeof window === 'undefined') return DEFAULTS
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    return migrate(JSON.parse(raw))
  } catch {
    return DEFAULTS
  }
}

function saveState(state) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {}
}

export function useIndicatorState() {
  const [state, setState] = useState(loadState)
  const remoteAppliedRef = useRef(false)
  const skipMountSaveRef = useRef(true)
  const skipNextSaveRef = useRef(false)
  const localChangedSinceMountRef = useRef(false)
  const stateRef = useRef(state)
  stateRef.current = state

  // Bug P0 (2026-08-12, "primera activación no se guarda en cuenta nueva"):
  // esta guarda gateaba el guardado remoto con `hydratedRef.current`, un
  // flag que sólo pasa a true cuando el GET inicial de /chart-preferences
  // resuelve (ver el otro efecto). Si el usuario activaba un indicador ANTES
  // de que ese GET resolviera (típico en una cuenta recién creada: primera
  // fila, conexión de BD fría → GET más lento que de costumbre), este efecto
  // se limitaba a `return` sin guardar NADA — y como el cambio de `state` ya
  // había ocurrido, nada disparaba un reintento después: el guardado remoto
  // de esa primera activación se perdía para siempre en esa sesión.
  // Fix: sólo se salta el guardado remoto en el PRIMER pase de este efecto
  // (el disparado por el montaje, cuando `state` es aún el snapshot de
  // localStorage y todavía no sabemos si el servidor ya tiene algo que
  // hidratar por encima) — igual que `isFirstSaveRef` en useIndicatorConfig.
  // Cualquier cambio real posterior, sea antes o después de que el GET
  // resuelva, se encola con saveRemote sin esperar a la hidratación: es
  // seguro hacerlo porque el backend hace un PATCH por clave (merge), no un
  // overwrite completo, así que no depende de haber visto ya el snapshot
  // remoto. `skipNextSaveRef` sigue evitando el eco de cuando SÍ llega
  // hidratación y pisa el estado local (ver el otro efecto).
  useEffect(() => {
    saveState(state)
    if (skipMountSaveRef.current) {
      skipMountSaveRef.current = false
      return
    }
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false
      return
    }
    // Cualquier guardado real (no el del montaje, no el eco de una
    // hidratación aplicada) es una acción del usuario: a partir de aquí el
    // estado local es más nuevo que lo que sabíamos del servidor.
    localChangedSinceMountRef.current = true
    saveRemote(STORAGE_KEY, state)
  }, [state])

  useEffect(() => {
    let cancelled = false
    getRemoteValue(STORAGE_KEY).then((remote) => {
      if (cancelled) return
      if (remoteAppliedRef.current) return
      // Bug P0 (2026-08-12, "F5 inmediato tras activar pierde el estado"):
      // un F5 real cancela cualquier PATCH todavía en vuelo (la navegación
      // aborta las peticiones del documento anterior) — ninguna solución de
      // solo-JS puede evitar esa cancelación. Lo que SÍ se puede evitar: que
      // la hidratación de la página recién cargada aplique un snapshot del
      // servidor más viejo que ese PATCH cancelado. `hasUnsyncedLocalWrite`
      // consulta un ledger en localStorage (sobrevive al F5): si la SESIÓN
      // ANTERIOR encoló un guardado para esta clave que nunca se confirmó
      // (cancelado por la navegación, o red caída), el estado local recién
      // cargado de localStorage es más nuevo que cualquier snapshot remoto
      // que pueda llegar ahora — se descarta la hidratación, se deja ganar
      // al local, y se reintenta el guardado que se perdió (nada más lo
      // habría reintentado: el mount no dispara saveRemote por diseño).
      // Mismo criterio si el cambio ocurrió DESPUÉS del montaje en esta
      // misma sesión (`localChangedSinceMountRef`, carrera dentro de la
      // misma carga de página, ya en vuelo — no hace falta reintentar aquí).
      if (localChangedSinceMountRef.current) return
      if (hasUnsyncedLocalWrite(STORAGE_KEY)) {
        remoteAppliedRef.current = true
        saveRemote(STORAGE_KEY, stateRef.current)
        return
      }
      if (!remote) return
      remoteAppliedRef.current = true
      const migrated = migrate(remote)
      // Si migrate() no cambió nada real (remoto ya estaba en la versión
      // actual, sin ids duplicados que reasignar, etc.) esto es un eco puro
      // de hidratación — se salta el guardado como siempre para no generar
      // un PATCH de vuelta sin necesidad. Pero si SÍ cambió algo (migración
      // de versión antigua, o el saneado de ids duplicados de arriba), ese
      // resultado migrado es estrictamente mejor que lo que hay en el
      // servidor y debe persistirse — si no, una cuenta con ids duplicados
      // (bug real, 2026-08-13: la cuenta de Marc) se "autocuraba" solo en
      // local en cada carga, generando ids nuevos aleatorios cada vez sin
      // que el servidor viera NUNCA la corrección, hasta que el usuario
      // tocase algo más. No se salta skipNextSaveRef en este caso: se deja
      // que el efecto de guardado de arriba dispare saveRemote con
      // normalidad, igual que un cambio real del usuario.
      if (JSON.stringify(migrated) === JSON.stringify(remote)) {
        skipNextSaveRef.current = true
      }
      setState(migrated)
    })
    return () => { cancelled = true }
  }, [])

  const setBuiltin = useCallback((key, value) => {
    if (key === 'showStructureBreaks') {
      setRegistry('choch_bos_quantum', value)
      return
    }
    setState(prev => ({ ...prev, [key]: value }))
  }, [])

  const toggleRegistry = useCallback((id) => {
    setState(prev => ({
      ...prev,
      activeRegistry: { ...prev.activeRegistry, [id]: !prev.activeRegistry[id] },
    }))
  }, [])

  const setRegistry = useCallback((id, active) => {
    setState(prev => ({
      ...prev,
      activeRegistry: { ...prev.activeRegistry, [id]: active },
    }))
  }, [])

  const addMovingAverage = useCallback((type, opts = {}) => {
    setState(prev => ({
      ...prev,
      movingAverages: [
        ...prev.movingAverages,
        {
          id: nextId(),
          type,
          period: opts.period ?? (type === 'ema' ? 20 : 200),
          color: opts.color ?? (type === 'ema' ? '#a855f7' : '#22c55e'),
          active: opts.active ?? true,
          lineStyle: opts.lineStyle ?? DEFAULT_LINE_STYLE,
          lineWidth: opts.lineWidth ?? DEFAULT_LINE_WIDTH,
        },
      ],
    }))
  }, [])

  const removeMovingAverage = useCallback((id) => {
    setState(prev => ({
      ...prev,
      movingAverages: prev.movingAverages.filter(ma => ma.id !== id),
    }))
  }, [])

  const updateMovingAverage = useCallback((id, patch) => {
    setState(prev => ({
      ...prev,
      movingAverages: prev.movingAverages.map(ma => (ma.id === id ? { ...ma, ...patch } : ma)),
    }))
  }, [])

  const toggleMovingAverage = useCallback((id, active) => {
    setState(prev => ({
      ...prev,
      movingAverages: prev.movingAverages.map(ma => (ma.id === id ? { ...ma, active } : ma)),
    }))
  }, [])

  const activeCount = useMemo(() => {
    const maActive = state.movingAverages.filter(ma => ma.active).length
    const registryActive = Object.values(state.activeRegistry).filter(Boolean).length
    const structureCore = [
      state.activeRegistry?.['choch_bos_quantum'] === true,
      state.activeRegistry?.['order_blocks_quantum'] === true,
    ].filter(Boolean).length
    const builtins = [
      state.showRsi, state.showVolume,
    ].filter(Boolean).length
    return maActive + registryActive + builtins + structureCore
  }, [state])

  return {
    state: state ?? DEFAULTS,
    showStructureBreaks: (state ?? DEFAULTS).activeRegistry?.['choch_bos_quantum'] === true,
    setBuiltin,
    toggleRegistry,
    setRegistry,
    addMovingAverage,
    removeMovingAverage,
    updateMovingAverage,
    toggleMovingAverage,
    activeCount,
  }
}
